import type { DecodedAuthToken, JwtLicenseClaim } from '../../components/authentication/types/AuthTypes';

const NO_LICENSE = 'No license assigned. Contact your administrator.';
const EXPIRED = 'License expired. Contact your administrator to renew.';

/**
 * Returns a blocking message for the app chrome, or null if the user should not see a hard block.
 * Admins bypass license blocks per backend contract.
 */
export function licenseBlockingMessage(
  license: JwtLicenseClaim | null | undefined,
  isAdminUser: boolean
): string | null {
  if (isAdminUser) return null;
  if (!license) return null;
  if (!license.has_license) return NO_LICENSE;
  if (!license.license_valid) return EXPIRED;
  return null;
}

/** Optional soft warning when the license is valid but expiring within `warnWithinDays`. */
export function licenseExpiryWarning(
  license: JwtLicenseClaim | null | undefined,
  isAdminUser: boolean,
  warnWithinDays = 30
): string | null {
  if (isAdminUser || !license?.license_expires_at || !license.license_valid) return null;
  const expiresAt = new Date(license.license_expires_at);
  if (Number.isNaN(expiresAt.getTime())) return null;
  const days = (expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  if (days > 0 && days < warnWithinDays) {
    return `Your license expires in ${Math.ceil(days)} day(s).`;
  }
  return null;
}

export function readLicenseFromDecoded(decoded: DecodedAuthToken): JwtLicenseClaim | null {
  return decoded.license ?? null;
}

export { NO_LICENSE, EXPIRED };
